<template>
  <f7-page :page-content="true" name="funciones" @page:beforein="entrar">
    <f7-navbar :back-link="$t('atras')">
      <f7-nav-title sliding>{{ $t('funciones.titulo') }}</f7-nav-title>
      <f7-nav-right>
        <f7-button icon-f7="arrow_clockwise" :text="$t('funciones.recargar')" class="display-flex" @click="cargar"></f7-button>
      </f7-nav-right>
    </f7-navbar>

    <f7-block v-if="error403" strong>
      <p>{{ $t('funciones.noAutorizado') }}</p>
    </f7-block>

    <template v-else>
      <!-- Alta en línea, encima de la tabla. La misma tarjeta sirve para editar. -->
      <f7-card class="padding-vertical-half padding-right bloque">
        <f7-card-content>
          <r-form ref="formFuncion" :key="formKey" v-slot="{ rform }" :initialData="editando">
            <f7-row no-gap class="list list-form no-hairlines">
              <f7-col width="100" medium="20">
                <ul>
                  <!-- Sólo puestos ACTIVOS: un puesto desactivado deja de ofrecerse aquí, que es
                       precisamente lo que significa desactivarlo.
                       El puesto va PRIMERO porque es lo primero que se elige: al elegirlo, la
                       aplicación rellena sola el código de la función (#483).

                       `alElegirPuesto()` va SIN ARGUMENTOS, y no es un descuido: `r-input` entrega
                       este mismo `change` DOS VECES y con cosas distintas —el valor, desde su
                       `setValue`, y el evento del DOM, porque reenvía `$listeners` al
                       `f7-list-input` que lleva dentro (`rInput.vue`, líneas 119-121)—. Pasando
                       `$event`, la segunda entrega pedía la propuesta con
                       `puestoId=[object Event]`. Escrito así, el argumento no puede llegar, y el
                       método lee el puesto de `formData`, que es lo único que dice lo mismo en las
                       dos entregas. -->
                  <r-input
                    outline
                    floating-label
                    :label="$t('funciones.puesto')"
                    :form="rform"
                    type="select"
                    name="puestoId"
                    @change="alElegirPuesto()"
                  >
                    <option :value="null">{{ $t('funciones.eligePuesto') }}</option>
                    <option v-for="p in puestosDisponibles" :key="p.id" :value="p.id">{{ p.codigo }} · {{ p.nombre }}</option>
                  </r-input>
                </ul>
              </f7-col>
              <f7-col width="100" medium="15">
                <ul>
                  <!-- `all-mayus`: el backend guarda el código en mayúsculas.
                       Ya NO es de sólo lectura al editar: el código se puede cambiar para
                       reordenar el catálogo (#483). Lo que no se toca nunca es el interno, que
                       se enseña al lado y no es un campo. -->
                  <r-input
                    outline
                    floating-label
                    all-mayus
                    :label="$t('funciones.codigo')"
                    :form="rform"
                    type="text"
                    name="codigo"
                  ></r-input>
                </ul>
              </f7-col>
              <f7-col width="100" medium="35">
                <ul>
                  <!-- Varias líneas y `resizable`: el nombre de una función no cabe en una línea
                       y antes no se veía entero mientras se escribía (#483). -->
                  <r-input
                    outline
                    floating-label
                    resizable
                    :label="$t('funciones.nombre')"
                    :form="rform"
                    type="textarea"
                    name="nombre"
                  ></r-input>
                </ul>
              </f7-col>
              <f7-col width="100" medium="30" class="display-flex justify-content-center align-items-center">
                <f7-button fill icon-f7="plus" :text="editando.id ? $t('funciones.guardar') : $t('funciones.nueva')" class="display-flex" @click="guardar"></f7-button>
                <f7-button v-if="editando.id" :text="$t('funciones.cancelar')" class="display-flex margin-left-half" @click="nueva"></f7-button>
              </f7-col>
            </f7-row>
          </r-form>
          <!-- El código interno de la función que se está editando: no es un campo, porque no se
               puede cambiar, pero tiene que verse también en la ficha (#483). -->
          <p v-if="editando.id" class="pie">
            <b>{{ $t('funciones.codigoInterno') }}:</b>
            <span class="codigo-interno">{{ editando.codigoInterno || '—' }}</span>
          </p>
          <!-- El puesto elegido no tiene abreviatura: se dice qué falta, quién lo desbloquea y
               dónde, junto al campo que se ha quedado vacío y no en un aviso de arriba. -->
          <p v-if="sinAbreviatura" class="pie aviso">{{ $t('funciones.sinAbreviatura') }}</p>
          <p class="pie">{{ editando.id ? $t('funciones.pieCodigoEditable') : $t('funciones.pieEjemplos') }}</p>
          <p class="pie">{{ $t('funciones.pieCodigoInterno') }}</p>
          <!-- Sin puestos activos no se puede dar de alta nada: se dice y se manda a la otra
               pantalla, en vez de dejar un desplegable vacío sin explicación. -->
          <p v-if="!puestosDisponibles.length" class="pie aviso">{{ $t('funciones.sinPuestos') }}</p>
        </f7-card-content>
      </f7-card>

      <f7-block strong class="tabla-wrapper">
        <table class="tabla-funciones">
          <thead>
            <tr>
              <th>{{ $t('funciones.codigoInterno') }}</th>
              <th>{{ $t('funciones.codigo') }}</th>
              <th>{{ $t('funciones.nombre') }}</th>
              <th>{{ $t('funciones.puesto') }}</th>
              <th>{{ $t('funciones.personasVigentes') }}</th>
              <th>{{ $t('funciones.estado') }}</th>
              <th>{{ $t('funciones.acciones') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="funcion in funciones" :key="funcion.id" :class="{ inactivo: !funcion.activo }">
              <td class="col-codigo">{{ funcion.codigoInterno || '—' }}</td>
              <td class="col-codigo">{{ funcion.codigo }}</td>
              <td class="col-nombre">{{ funcion.nombre }}</td>
              <td>
                <span>{{ funcion.puestoNombre || $t('funciones.sinPuesto') }}</span>
                <span v-if="funcion.puestoCodigo" class="puesto-codigo">({{ funcion.puestoCodigo }})</span>
                <span
                  v-if="tieneAviso(funcion, 'PUESTO_INACTIVO')"
                  class="etiqueta-aviso"
                >{{ $t('funciones.avisos.PUESTO_INACTIVO') }}</span>
              </td>
              <td class="col-personas">{{ nombresPersonas(funcion) }}</td>
              <td>
                <span class="etiqueta-estado" :class="funcion.activo ? 'estado-activo' : 'estado-inactivo'">
                  {{ funcion.activo ? $t('funciones.activa') : $t('funciones.inactiva') }}
                </span>
              </td>
              <td class="col-acciones">
                <a href="#" @click.prevent="editar(funcion)">{{ $t('funciones.editar') }}</a>
                <a v-if="funcion.activo" href="#" @click.prevent="cambiarActivo(funcion, false)">{{ $t('funciones.desactivar') }}</a>
                <a v-else href="#" @click.prevent="cambiarActivo(funcion, true)">{{ $t('funciones.reactivar') }}</a>
              </td>
            </tr>
            <tr v-if="!funciones.length">
              <td colspan="7" class="vacio">{{ $t('funciones.sinResultados') }}</td>
            </tr>
          </tbody>
        </table>
      </f7-block>

      <p class="pie">{{ $t('funciones.pie') }}</p>
    </template>
  </f7-page>
</template>
<style scoped>
.bloque .pie,
.pie { font-size: 12px; color: #8e8e93; margin: 0 16px 8px; }
.pie.aviso { color: #b26a00; }
.tabla-wrapper { overflow-x: auto; }
.tabla-funciones { width: 100%; border-collapse: collapse; font-size: 13px; }
.tabla-funciones th,
.tabla-funciones td {
  border-bottom: 1px solid #e0e0e0;
  padding: 4px 6px;
  text-align: left;
  vertical-align: top;
}
.tabla-funciones th { background-color: #f4f5f7; font-weight: bold; }
.tabla-funciones .col-codigo { font-family: monospace; white-space: nowrap; }
/* El nombre se lee ENTERO: pasa a la línea siguiente en vez de cortarse (#483). `word-break`
   para que una palabra más larga que la columna tampoco desborde la tabla. */
.tabla-funciones .col-nombre { white-space: normal; word-break: break-word; min-width: 180px; }
.codigo-interno { font-family: monospace; margin-left: 4px; }
.tabla-funciones .col-personas { max-width: 280px; word-break: break-word; }
.tabla-funciones .col-acciones a { margin-right: 8px; white-space: nowrap; }
.tabla-funciones tr.inactivo td { color: #9aa1a8; font-style: italic; }
.tabla-funciones .vacio { text-align: center; color: #8e8e93; padding: 16px; }
.tabla-funciones .puesto-codigo { color: #8e8e93; margin-left: 4px; }
.etiqueta-estado,
.etiqueta-aviso {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: bold;
  color: #fff;
  background-color: #8e8e93;
  margin-left: 4px;
}
.etiqueta-aviso { background-color: #ff9500; }
.estado-activo { background-color: #4cd964; }
.estado-inactivo { background-color: #8e8e93; }
</style>
<script>
import rForm from "./../../components/rForm.vue";
import rInput from "./../../components/rInput.vue";
import RhFunciones from "./../../js/db/RhFunciones.js";
import RhPuestos from "./../../js/db/RhPuestos.js";
import carrilPeticiones from "./../../js/utils/carrilPeticiones.js";
import { esperaDelCarril } from "./../../js/utils/esperaGlobal.js";
import preloaderDeLaSesion from "./../../js/utils/preloaderDeLaSesion.js";

/**
 * Ajustes · Funciones (#50). El catálogo de funciones, cada una colgando de un puesto que se
 * elige del desplegable de puestos activos.
 *
 * Como en Puestos de trabajo, no hay borrado: una función se desactiva y se conserva en los
 * históricos (trazabilidad 17020).
 *
 * «Personas vigentes» son quienes la desempeñan hoy según la tarjeta «Puesto y funciones» de su
 * ficha (#51): quien la finalizó deja de contar el mismo día.
 *
 * Dos códigos por función desde la #483, y hacen cosas distintas:
 *   · el INTERNO (FN-0001) lo pone la aplicación, no se puede editar y es con el que se enlazan
 *     las personas, el histórico y las reglas de coincidencia;
 *   · el de la FUNCIÓN (F-DIR-49) lo propone la aplicación con la abreviatura del puesto en
 *     cuanto se elige el puesto, y sí se puede cambiar para reordenar el catálogo.
 * Intercambiar dos códigos no se puede desde aquí —pasaría por un «código repetido»—: hay que
 * cambiarlos a la vez, y eso lo hará el asesor IA de la #484.
 */
export default {
  components: {
    rForm,
    rInput
  },
  data: function() {
    return {
      funciones: [],
      puestos: [],
      editando: this.enBlanco(),
      formKey: 0,
      // El puesto elegido no tiene abreviatura, así que la aplicación no puede proponer código.
      sinAbreviatura: false,
      error403: false
    };
  },
  created: function() {
    // La ruta lleva `keepAlive`, así que salir no destruye la pantalla: se sale, se vuelve y la
    // carga anterior sigue viva. El carril hace que solo conteste la última (ver
    // carrilPeticiones.js). Fuera de `data` porque no es estado que se pinte.
    //
    // #472 · Y el preloader lo enciende y lo apaga el carril: colgado del `then` de cada carga, la
    // respuesta descartada de una anterior lo apagaba con la vigente todavía en vuelo. Va por un
    // testigo de `esperaGlobal`, igual que las escrituras de esta pantalla, para que el `then` de
    // un guardado —que recarga— no le quite el indicador a esa recarga.
    this.carril = carrilPeticiones({ cargando: esperaDelCarril(this) });
    // El de la propuesta de código va aparte del de la carga: son peticiones distintas y una no
    // releva a la otra. Su sello de sesión es `formKey`, o sea QUÉ formulario hay delante: ver
    // `alElegirPuesto`.
    this.carrilCodigo = carrilPeticiones();
    // El puesto que este formulario ya ha atendido, para que la entrega repetida del `change` de
    // `r-input` no pida la propuesta dos veces (ver `alElegirPuesto`). Fuera de `data` como los
    // carriles: no se pinta.
    this.puestoAtendido = undefined;
  },
  computed: {
    /**
     * Los puestos que ofrece el desplegable: los activos, y además el de la función que se está
     * editando aunque esté desactivado. Si no, al abrir esa función el `select` no encontraría
     * su valor, se quedaría en blanco y guardar la movería de puesto sin que nadie lo pidiera.
     */
    puestosDisponibles: function() {
      const self = this;
      const activos = self.puestos.filter(function(p) { return p.activo; });
      const actual = self.editando.puestoId;
      if (actual && !activos.some(function(p) { return p.id == actual; })) {
        const suyo = self.puestos.filter(function(p) { return p.id == actual; });
        return suyo.concat(activos);
      }
      return activos;
    }
  },
  methods: {
    enBlanco: function() {
      return { id: null, codigoInterno: null, codigo: null, nombre: null, puestoId: null };
    },
    es403: function(e) {
      return !!(e && (e.status == 403 || (e.xhr && e.xhr.status == 403)));
    },
    tieneAviso: function(funcion, aviso) {
      return (funcion.avisos || []).indexOf(aviso) >= 0;
    },
    nombresPersonas: function(funcion) {
      const personas = funcion.personasVigentes || [];
      if (!personas.length) return '—';
      return personas.map(function(p) { return p.nombre || p.username; }).join(', ');
    },
    nueva: function() {
      this.formKey++;
      this.abrirFormulario();
      this.editando = this.enBlanco();
      this.sinAbreviatura = false;
    },
    /**
     * Declara al carril de la propuesta QUÉ formulario está delante, y es lo único que hace falta
     * para que una propuesta en vuelo no caiga en el formulario siguiente. `formKey` sube cada vez
     * que el formulario se recrea —al empezar una nueva y al abrir una función para editarla—, así
     * que cambiar de formulario invalida de golpe lo que estuviera pidiéndose.
     *
     * Va por el carril y no por un `if` al lado, como en el resto del front: comparar en el
     * callback «¿sigue siendo este puesto?» no distingue dos funciones del MISMO puesto, y
     * entonces la propuesta de la primera pisaba el código real de la segunda, que es lo que se
     * guardaba (revisión CODEX del PR #1788).
     */
    abrirFormulario: function() {
      this.carrilCodigo.sesion(this.formKey);
      // Y el formulario nuevo no ha atendido ningún puesto todavía, aunque abra con uno puesto:
      // el de la función que se edita no se ha elegido, se ha cargado. Ver `alElegirPuesto`.
      this.puestoAtendido = undefined;
    },
    /**
     * El código que hay escrito en un formulario, en forma de comparar. Vacío y sin escribir son
     * lo mismo: el campo pasa de `null` a `''` en cuanto alguien escribe y borra, y eso no es
     * haber escrito un código distinto.
     */
    codigoEscrito: function(formulario) {
      const valor = formulario && formulario.formData ? formulario.formData.codigo : null;
      return valor === null || valor === undefined ? '' : String(valor);
    },
    /**
     * Entrar en la pantalla. No es `cargar` directamente porque con `keepAlive` el formulario
     * sobrevive a salir: se volvía con la función de antes a medio editar y el código bloqueado.
     * El botón «Recargar» sí llama solo a `cargar`, que ahí vaciar lo escrito sería el fallo.
     */
    entrar: function() {
      this.nueva();
      this.cargar();
    },
    editar: function(funcion) {
      this.formKey++;
      this.abrirFormulario();
      this.editando = {
        id: funcion.id,
        codigoInterno: funcion.codigoInterno,
        codigo: funcion.codigo,
        nombre: funcion.nombre,
        puestoId: funcion.puestoId
      };
      // Se abre con su propio puesto, así que no hay nada que proponer todavía: el aviso de
      // «este puesto no tiene abreviatura» solo aplica a lo que se elija a partir de ahora.
      this.sinAbreviatura = false;
    },
    /**
     * Al elegir el puesto, la aplicación rellena sola el código de la función con el siguiente
     * libre de ese puesto (#483). Volver al puesto que la función ya tenía devuelve su código,
     * que es lo que hace el servidor: solo cambia el código si de verdad cambia de puesto.
     *
     * **No recibe el valor: lo lee de `formData`.** `r-input` entrega el mismo `change` DOS VECES
     * —el valor desde su `setValue`, y después el evento del DOM, porque reenvía `$listeners` al
     * `f7-list-input` (`rInput.vue`, líneas 119-121)—, así que lo que llega en el argumento es una
     * cosa o la otra según la entrega: la segunda pedía la propuesta con `puestoId=[object Event]`
     * y, siendo la última del carril, era la que contestaba (revisión CODEX del PR #1788, rondas 1
     * y 3). `formData` es lo único que dice lo mismo en las dos —`setValue` lo escribe ANTES de
     * emitir—, así que cuántas veces llegue el evento, y con qué, deja de decidir nada. Es lo que
     * hace `changeRol` en `usuario-form.vue`, y por eso la plantilla llama sin argumentos: que el
     * argumento no pueda llegar es más seguro que acordarse de no leerlo.
     *
     * Y la entrega repetida no sale dos veces por la red: `puestoAtendido` recuerda qué puesto ha
     * atendido ya este formulario. Un `select` no emite `change` al reelegir la opción que ya
     * estaba, así que esto no se come ninguna elección de verdad.
     *
     * Quién puede escribir el código lo decide el carril (ver `abrirFormulario`), y no una
     * comprobación en el callback: así hay un solo sitio donde se decide, y los dos criterios
     * —«es la última elección de este formulario» y «el formulario sigue siendo este»— no se
     * pueden desincronizar.
     */
    alElegirPuesto: function() {
      const self = this;
      const app = self.$f7;
      const formulario = self.$refs.formFuncion;
      if (!formulario) return;
      const puestoId = formulario.formData ? formulario.formData.puestoId : null;
      // La misma elección entregada dos veces es UNA elección. Va aquí y no en cada rama porque
      // las tres —quitar el puesto, volver al suyo y pedir la propuesta— tocan el carril o el
      // campo, y repetirlas es repetir un efecto, no comprobar lo mismo dos veces.
      if (puestoId === self.puestoAtendido) return;
      self.puestoAtendido = puestoId;
      if (!puestoId) {
        // Quitar el puesto también manda sobre lo que estuviera pidiéndose: si no, la propuesta
        // en vuelo llegaba después y escribía un código del puesto que se acaba de quitar.
        self.carrilCodigo.invalidar();
        self.sinAbreviatura = false;
        return;
      }
      if (self.editando.id && puestoId == self.editando.puestoId) {
        // Y volver al puesto que la función ya tenía, igual: su código es el que manda, no la
        // propuesta que se pidió para el puesto por el que se pasó en medio.
        self.carrilCodigo.invalidar();
        self.sinAbreviatura = false;
        formulario.formData.codigo = self.editando.codigo;
        return;
      }
      // El aviso se apaga al EMPEZAR la petición, no al contestarla: si el puesto anterior no
      // tenía abreviatura y la petición de este falla, el aviso se quedaba encendido diciendo del
      // puesto nuevo algo que nadie ha comprobado (revisión CODEX del PR #1788).
      self.sinAbreviatura = false;
      // Lo que hay escrito en el campo al pedir la propuesta. La propuesta solo lo sustituye si
      // al llegar sigue siendo esto: quien escriba un código a mano mientras se pedía manda sobre
      // el servidor. La pantalla anuncia que el código se puede cambiar, así que cuál se guarda no
      // puede depender de lo que tarde la respuesta (revisión CODEX del PR #1788).
      const codigoAlPedir = self.codigoEscrito(formulario);
      self.carrilCodigo.lanzar(RhFunciones.siguienteCodigo(puestoId), {
        exito: function(res) {
          const abierto = self.$refs.formFuncion;
          if (!abierto) return;
          // El aviso es del PUESTO, así que se pone aunque el código escrito a mano se quede: es
          // lo que explica por qué no ha salido ninguna propuesta.
          self.sinAbreviatura = !!(res && res.aviso == 'PUESTO_SIN_ABREVIATURA');
          if (self.codigoEscrito(abierto) !== codigoAlPedir) return;
          abierto.formData.codigo = (res && res.codigo) || null;
        },
        // Sin `limpiar`: lo que haya en el campo se queda. Vaciarlo porque no se ha podido
        // proponer nada borraría un código que quien está editando pudo escribir a mano.
        fallo: function(e) {
          app.dialog.alert(e && e.message ? e.message : self.$t('funciones.errorSiguienteCodigo'));
        }
      });
    },
    guardar: function() {
      const self = this;
      const app = self.$f7;
      const datos = self.$refs.formFuncion ? self.$refs.formFuncion.formData : {};
      // El código ya no se exige aquí: lo propone la aplicación al elegir el puesto, y si el
      // puesto no tiene abreviatura el backend explica qué falta y dónde se arregla. Pedirlo
      // aquí dejaría a quien lo lea creyendo que tiene que inventárselo.
      if (!datos.nombre || !datos.nombre.trim() || !datos.puestoId) {
        app.dialog.alert(self.$t('funciones.faltanDatos'));
        return;
      }
      const cuerpo = { codigo: datos.codigo, nombre: datos.nombre, puestoId: datos.puestoId };
      // #663 · La espera de la sesión, y no un testigo a pelo: si la sesión cambia en vuelo, la
      // frontera calla el `then` que la soltaba, y el testigo se quedaba en la cuenta común con el
      // indicador puesto para quien entrase. Esta se suelta también al cerrar la sesión.
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      const peticion = self.editando.id
        ? RhFunciones.editar(self.editando.id, cuerpo)
        : RhFunciones.crear(cuerpo);
      peticion
        .then(function() {
          app.toast.create({ text: self.$t('funciones.guardada'), position: 'center', closeTimeout: 2500 }).open();
          self.nueva();
          self.cargar();
        })
        .catch(function(e) {
          app.dialog.alert(e && e.message ? e.message : self.$t('funciones.errorGuardando'));
        })
        .then(function() {
          espera.cerrar();
        });
    },
    cambiarActivo: function(funcion, activo) {
      const self = this;
      const app = self.$f7;
      if (!activo) {
        app.dialog.confirm(self.$t('funciones.confirmaDesactivar'), self.$t('funciones.titulo'),
          function() { self.enviarActivo(funcion, false); });
        return;
      }
      self.enviarActivo(funcion, true);
    },
    enviarActivo: function(funcion, activo) {
      const self = this;
      const app = self.$f7;
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      RhFunciones.cambiarActivo(funcion.id, activo, null)
        .then(function() {
          self.cargar();
        })
        .catch(function(e) {
          app.dialog.alert(e && e.message ? e.message : self.$t('funciones.errorEstado'));
        })
        .then(function() {
          espera.cerrar();
        });
    },
    cargar: function() {
      const self = this;
      const app = self.$f7;
      // Las dos listas van juntas: el desplegable de puestos se pinta con la tabla, y pedirlas
      // por separado dejaría una pantalla a medias si falla una.
      // Los puestos se piden TODOS, no sólo los activos: el desplegable ofrece los activos,
      // pero al editar una función cuyo puesto se desactivó hace falta tener ese puesto para
      // poder enseñarlo seleccionado (ver `puestosDisponibles`).
      self.carril.lanzar(Promise.all([RhFunciones.listar(false, null), RhPuestos.listar(false)]), {
        exito: function(res) {
          // Una carga que va bien manda sobre el 403 de cualquier intento anterior: con
          // `keepAlive` la pantalla no se recrea, así que el «No autorizado» se quedaba puesto
          // para siempre aunque «Recargar» trajera ya los datos (revisión CODEX del PR #1731).
          self.error403 = false;
          self.funciones = res[0] || [];
          self.puestos = res[1] || [];
        },
        // Guardar, desactivar y reactivar acaban llamando aquí: si falla esta carga, lo que queda
        // en pantalla es el catálogo de ANTES de la acción, con la fila recién desactivada
        // enseñándose activa. Se vacía y se dice que no se pudo cargar.
        limpiar: function() {
          self.funciones = [];
          self.puestos = [];
        },
        fallo: function(e) {
          if (self.es403(e)) self.error403 = true;
          else app.dialog.alert(e && e.message ? e.message : self.$t('funciones.errorCargando'));
        }
      });
    }
  }
};
</script>
